import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Plus, Send, Loader2, Save, ArrowLeft, CheckCircle2, Circle, Clock, Download, Info, Trash2, AlertTriangle } from "lucide-react";
import { AttachmentsPanel } from "./relazioni/sectionHelpers";
import { DynamicSection } from "./relazioni/DynamicSection";

const MONTHS = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const MONTHS_FULL = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => CURRENT_YEAR - 1 + i);
const COMPLETION_THRESHOLD = 95;

const STATO_BADGE = {
  bozza: { l: "Bozza", cls: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200" },
  completata: { l: "Completata", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
  inviata: { l: "Inviata", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
};

// Ordina e filtra le sezioni visibili dello schema
function visibleSections(schema) {
  if (!schema || !Array.isArray(schema.sezioni)) return [];
  return schema.sezioni
    .filter((s) => s.visibile !== false)
    .slice()
    .sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
}

// Rileva campi presenti nei dati salvati ma non più definiti nello schema (legacy)
function detectLegacyKeys(report, schema) {
  if (!report?.sections || !schema) return [];
  const legacy = [];
  const sezMap = Object.fromEntries((schema.sezioni || []).map((s) => [s.key, new Set((s.campi || []).map((c) => c.key))]));
  for (const [sezKey, sezData] of Object.entries(report.sections)) {
    if (!sezData || typeof sezData !== "object") continue;
    const known = sezMap[sezKey];
    if (!known) {
      legacy.push(sezKey);
      continue;
    }
    for (const fieldKey of Object.keys(sezData)) {
      if (fieldKey.startsWith("ai_")) continue;
      if (fieldKey === "righe") continue;
      if (fieldKey === "interventi_sospesi") continue;
      if (!known.has(fieldKey)) legacy.push(`${sezKey}.${fieldKey}`);
    }
  }
  return legacy;
}

// Utility: calcola i campi required visibili non compilati di una sezione (lato client)
function computeMissingFields(sez, data) {
  if (!sez) return [];
  const out = [];
  const campi = (sez.campi || []).filter((c) => c.visibile !== false);
  for (const c of campi) {
    if (!c.required) continue;
    const v = (data || {})[c.key];
    if (c.tipo === "tabella_mensile") {
      const righe = v?.righe || (Array.isArray(v) ? v : []);
      const filled = righe.some((r) => r && Object.entries(r).some(([k, val]) =>
        !["mese", "stato", "_sibling_report_id"].includes(k) && val != null && val !== ""));
      if (!filled) out.push({ key: c.key, label: c.label });
    } else if (c.tipo === "array_ripetibile") {
      if (!Array.isArray(v) || v.length === 0) out.push({ key: c.key, label: c.label });
    } else {
      const empty = v == null || (typeof v === "string" && v.trim() === "");
      if (empty) out.push({ key: c.key, label: c.label });
    }
  }
  return out;
}

async function downloadPdf(id, hotelNome, mese, anno) {
  try {
    const res = await api.get(`/monthly-reports/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    const safeHotel = (hotelNome || "hotel").replace(/[^a-zA-Z0-9]+/g, "_");
    a.download = `Relazione_${safeHotel}_${MONTHS_FULL[mese - 1] || mese}_${anno}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Download avviato");
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

// -------- LIST --------
export function RelazioniMensiliList() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newForm, setNewForm] = useState({ hotel_id: "", mese: new Date().getMonth() + 1, anno: CURRENT_YEAR });
  const [creating, setCreating] = useState(false);
  const [fHotel, setFHotel] = useState("");
  const [fAnno, setFAnno] = useState("");
  const navigate = useNavigate();
  const canCreate = ["super_admin", "resident_manager"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (fHotel) q.append("hotel_id", fHotel);
      if (fAnno) q.append("anno", fAnno);
      const [r, h, s] = await Promise.all([
        api.get(`/monthly-reports?${q}`),
        api.get("/hotels"),
        api.get("/admin/report-schema").catch(() => null),
      ]);
      setReports(r.data || []);
      setHotels(h.data || []);
      if (s) setSchema(s.data);
    } finally { setLoading(false); }
  }, [fHotel, fAnno]);
  useEffect(() => { load(); }, [load]);

  const sectionsList = useMemo(() => visibleSections(schema), [schema]);
  const totalSections = sectionsList.length || 1;

  const create = async () => {
    if (!newForm.hotel_id) return toast.error("Seleziona hotel");
    setCreating(true);
    try {
      const { data } = await api.post("/monthly-reports", newForm);
      toast.success("Relazione creata");
      setCreateOpen(false);
      navigate(`/relazioni/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const hotelName = (hid) => hotels.find((h) => h.id === hid)?.nome || "—";

  return (
    <div className="space-y-5" data-testid="relazioni-v2-list">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display tracking-tight flex items-center gap-2"><FileText className="h-5 w-5 text-lavender-600" /> Relazioni Mensili</h1>
          <p className="text-sm text-muted-foreground mt-1">Relazioni strutturate con confronto automatico vs obiettivi hotel.</p>
        </div>
        {canCreate && (
          <Button onClick={() => { setNewForm({ hotel_id: hotels[0]?.id || "", mese: new Date().getMonth() + 1, anno: CURRENT_YEAR }); setCreateOpen(true); }} data-testid="new-report-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova relazione
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={fHotel || "__all__"} onValueChange={(v) => setFHotel(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="filter-hotel"><SelectValue placeholder="Tutti gli hotel" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">Tutti gli hotel</SelectItem>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fAnno || "__all__"} onValueChange={(v) => setFAnno(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-32" data-testid="filter-year"><SelectValue placeholder="Tutti gli anni" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">Tutti</SelectItem>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {reports.length === 0 && <p className="col-span-full text-sm text-muted-foreground text-center py-8">Nessuna relazione. Creane una nuova per iniziare.</p>}
          {reports.map((r) => {
            const avg = sectionsList.map((s) => r.completion?.[s.key] || 0).reduce((a, b) => a + b, 0) / totalSections;
            const b = STATO_BADGE[r.stato] || STATO_BADGE.bozza;
            return (
              <Link key={r.id} to={`/relazioni/${r.id}`} className="ih-glass rounded-2xl p-5 ih-card-hover relative" data-testid={`report-card-${r.id}`}>
                <button
                  className="absolute top-3 right-3 h-7 w-7 rounded-lg bg-background/70 hover:bg-lavender-100 dark:hover:bg-lavender-950/40 flex items-center justify-center transition-colors"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadPdf(r.id, hotelName(r.hotel_id), r.mese, r.anno); }}
                  title="Scarica PDF"
                  data-testid={`download-pdf-list-${r.id}`}
                >
                  <Download className="h-3.5 w-3.5 text-lavender-600" />
                </button>
                <div className="flex items-center justify-between mb-2 pr-8">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{MONTHS[r.mese - 1]} {r.anno}</div>
                  <div className="flex items-center gap-1">
                    {r.stato === "inviata" && r.schema_snapshot?.version && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-lavender-50 text-lavender-700 dark:bg-lavender-950/40 dark:text-lavender-300 font-medium tabular-nums" title="Versione schema al momento dell'invio">
                        schema v{r.schema_snapshot.version}
                      </span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.cls}`}>{b.l}</span>
                  </div>
                </div>
                <div className="font-medium">{hotelName(r.hotel_id)}</div>
                <div className="text-xs text-muted-foreground mt-1">Manager: {r.manager_nome_snapshot || "—"}</div>
                <div className="mt-3 h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                  <div className={`h-full ${avg >= COMPLETION_THRESHOLD ? "bg-emerald-500" : avg > 0 ? "bg-lavender-500" : "bg-slate-300"}`} style={{ width: `${Math.min(100, avg)}%` }} />
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">Compilazione: {Math.round(avg)}%</div>
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-report-modal">
          <DialogHeader><DialogTitle>Nuova relazione mensile</DialogTitle><DialogDescription>Seleziona hotel, mese e anno.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Hotel</label>
              <Select value={newForm.hotel_id} onValueChange={(v) => setNewForm({ ...newForm, hotel_id: v })}>
                <SelectTrigger data-testid="new-hotel"><SelectValue /></SelectTrigger>
                <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Mese</label>
                <Select value={String(newForm.mese)} onValueChange={(v) => setNewForm({ ...newForm, mese: parseInt(v) })}>
                  <SelectTrigger data-testid="new-mese"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Anno</label>
                <Select value={String(newForm.anno)} onValueChange={(v) => setNewForm({ ...newForm, anno: parseInt(v) })}>
                  <SelectTrigger data-testid="new-anno"><SelectValue /></SelectTrigger>
                  <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={create} disabled={creating} data-testid="create-confirm">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- EDITOR --------
export function RelazioneEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [targets, setTargets] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [globalSchema, setGlobalSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [sectionData, setSectionData] = useState({});
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, sRes] = await Promise.all([
        api.get(`/monthly-reports/${id}`),
        api.get("/admin/report-schema").catch(() => null),
      ]);
      setReport(data);
      if (sRes) setGlobalSchema(sRes.data);
      if (data.hotel_id) {
        const [hRes, tRes, siRes] = await Promise.all([
          api.get(`/hotels/${data.hotel_id}`).catch(() => null),
          api.get(`/admin/hotel-targets/${data.hotel_id}?anno=${data.anno}`).catch(() => null),
          api.get(`/monthly-reports?hotel_id=${data.hotel_id}&anno=${data.anno}&stato=inviata`).catch(() => null),
        ]);
        if (hRes) setHotel(hRes.data);
        if (tRes && tRes.data) setTargets(tRes.data);
        if (siRes) setSiblings((siRes.data || []).filter((r) => r.id !== data.id));
      }
    } catch (e) { toast.error(formatApiError(e)); navigate("/relazioni"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  // Schema effettivo: snapshot se relazione inviata, altrimenti globale
  const effectiveSchema = useMemo(() => {
    if (report?.schema_snapshot) return report.schema_snapshot;
    return globalSchema;
  }, [report, globalSchema]);

  const sections = useMemo(() => visibleSections(effectiveSchema), [effectiveSchema]);
  const totalSections = sections.length || 1;

  // Inizializza activeSection quando lo schema arriva
  useEffect(() => {
    if (!activeSection && sections.length > 0) {
      setActiveSection(sections[0].key);
    }
  }, [sections, activeSection]);

  useEffect(() => {
    if (report && activeSection) {
      setSectionData(report.sections?.[activeSection] || {});
    }
  }, [activeSection, report]);

  const legacyKeys = useMemo(() => detectLegacyKeys(report, effectiveSchema), [report, effectiveSchema]);

  const isReadOnly = report?.stato === "inviata";

  const save = async () => {
    if (isReadOnly) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/monthly-reports/${id}`, { section: activeSection, data: sectionData });
      setReport((r) => ({ ...r, completion: data.completion, sections: { ...r.sections, [activeSection]: sectionData } }));
      // v123 — Sibling override: toast dettagliato + reload siblings per vedere l'aggiornamento
      const overrides = Array.isArray(data.overrides_applied) ? data.overrides_applied : [];
      if (overrides.length > 0) {
        const mesiSet = new Set(overrides.map((o) => o.mese));
        const monthNames = [
          "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
          "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
        ];
        const mesiFmt = Array.from(mesiSet)
          .sort((a, b) => a - b)
          .map((m) => monthNames[m - 1])
          .join(", ");
        toast.success(
          `Dato propagato alla relazione di ${mesiFmt} (${overrides.length} campo${overrides.length === 1 ? "" : "/i"})`,
          { duration: 5000 }
        );
        // Reload siblings + report corrente per riflettere l'override via merge
        if (report?.hotel_id) {
          try {
            const siRes = await api.get(`/monthly-reports?hotel_id=${report.hotel_id}&anno=${report.anno}&stato=inviata`);
            setSiblings((siRes.data || []).filter((rr) => rr.id !== id));
            const refr = await api.get(`/monthly-reports/${id}`);
            setReport(refr.data);
            setSectionData(refr.data.sections?.[activeSection] || {});
          } catch { /* best effort */ }
        }
      } else {
        toast.success("Salvato");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doSubmit = async () => {
    setSubmitting(true); setSubmitError(null);
    try {
      const { data } = await api.post(`/monthly-reports/${id}/submit`);
      toast.success("Relazione inviata");
      setSubmitOpen(false);
      setReport((r) => ({ ...r, stato: "inviata", inviata_at: data.inviata_at, schema_snapshot: data.schema_snapshot || r.schema_snapshot }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object") setSubmitError(detail);
      else toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const onAttachmentsChange = (next) => setReport((r) => ({ ...r, attachments: next }));

  if (loading || !report || !effectiveSchema || !activeSection) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-6 w-6 animate-spin text-lavender-500" /></div>;
  }

  const activeSez = sections.find((s) => s.key === activeSection) || sections[0];
  const compCurrent = report.completion?.[activeSez.key] || 0;
  const avgAll = sections.map((s) => report.completion?.[s.key] || 0).reduce((a, b) => a + b, 0) / totalSections;
  const belowThreshold = sections.filter((s) => (report.completion?.[s.key] || 0) < COMPLETION_THRESHOLD);

  return (
    <div className="space-y-4 pb-24" data-testid="relazione-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/relazioni")}><ArrowLeft className="h-4 w-4" /> Lista</Button>
        <div className="text-xs text-muted-foreground">{MONTHS[report.mese - 1]} {report.anno} · {hotel?.nome || "—"}</div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadPdf(report.id, hotel?.nome, report.mese, report.anno)} data-testid="download-pdf-btn">
            <Download className="h-4 w-4" /> Scarica PDF
          </Button>
          {user?.role === "super_admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 border-rose-300/60"
              data-testid="relazione-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
            </Button>
          )}
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATO_BADGE[report.stato].cls}`}>{STATO_BADGE[report.stato].l}</span>
        </div>
      </div>

      {/* Delete relazione modal (SA only, v99+) */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="ih-glass-strong sm:max-w-md" data-testid="relazione-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminare la relazione?
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare la relazione <strong className="text-foreground">{MONTHS[report.mese - 1]} {report.anno} — {hotel?.nome || "—"}</strong>. Questa operazione è <strong>irreversibile</strong> — testo, allegati e valutazioni AI andranno persi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annulla</Button>
            <Button
              data-testid="relazione-delete-confirm"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  const { data } = await api.delete(`/monthly-reports/${id}`);
                  toast.success(`Relazione eliminata (${data.deleted_attachments} allegati)`);
                  setDeleteOpen(false);
                  navigate("/relazioni");
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally { setDeleting(false); }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isReadOnly && (
        <Card className="p-3 bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-200/60 text-xs text-emerald-800 dark:text-emerald-200">
          Relazione inviata il {report.inviata_at?.slice(0, 10)} — sola lettura.
          {report.schema_snapshot && (
            <span className="ml-1 opacity-80">Schema snapshot v{report.schema_snapshot.version} usato al momento dell&apos;invio.</span>
          )}
        </Card>
      )}

      {!isReadOnly && legacyKeys.length > 0 && (
        <Card className="p-3 bg-amber-50/40 dark:bg-amber-950/20 border-amber-200/60 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="legacy-banner">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Rilevati {legacyKeys.length} valori appartenenti a campi non più presenti nello schema corrente
            (campi legacy). Non verranno mostrati nel form. Restano salvati nel database e nell&apos;eventuale PDF/AI.
          </span>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
        {/* Stepper laterale — generato dallo schema */}
        <div className="space-y-1">
          {sections.map((s) => {
            const c = report.completion?.[s.key] || 0;
            const isActive = s.key === activeSection;
            const iconEl = c >= COMPLETION_THRESHOLD
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              : c > 0
                ? <Clock className="h-3.5 w-3.5 text-lavender-600" />
                : <Circle className="h-3.5 w-3.5 text-muted-foreground" />;
            return (
              <button key={s.key} onClick={() => setActiveSection(s.key)} data-testid={`tab-${s.key}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${isActive ? "bg-lavender-500 text-white" : "hover:bg-accent"}`}>
                {iconEl}
                <span className="flex-1 truncate">{s.nome}</span>
                <span className={`text-[10px] tabular-nums ${isActive ? "text-white/80" : "text-muted-foreground"}`}>{c}%</span>
              </button>
            );
          })}
        </div>

        {/* Form sezione — renderer dinamico */}
        <div className="space-y-3">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display tracking-tight">{activeSez.nome}</h2>
                <div className="text-xs text-muted-foreground">Compilazione sezione: <span className="tabular-nums font-medium">{compCurrent}%</span></div>
              </div>
              {!isReadOnly && (
                <Button onClick={save} disabled={saving} data-testid="save-section">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva sezione</Button>
              )}
            </div>

            <DynamicSection
              sezione={activeSez}
              data={sectionData}
              setData={setSectionData}
              isReadOnly={isReadOnly}
              targets={targets}
              siblings={siblings}
              meseCorrente={report.mese}
              userRole={user?.role}
              reportId={report.id}
            />

            <AttachmentsPanel
              reportId={report.id}
              section={activeSez.key}
              attachments={report.attachments || []}
              onChange={onAttachmentsChange}
              isReadOnly={isReadOnly}
            />
          </Card>
        </div>
      </div>

      {!isReadOnly && (
        <div className="fixed bottom-0 left-0 right-0 md:left-[var(--sidebar-w,16rem)] z-40 ih-glass-strong border-t border-border/60 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            Compilazione media: <span className="tabular-nums font-medium">{Math.round(avgAll)}%</span> su {totalSections} sezioni
          </div>
          <Button onClick={() => setSubmitOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="submit-btn">
            <Send className="h-4 w-4" /> Invia relazione
          </Button>
        </div>
      )}

      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="sm:max-w-md" data-testid="submit-modal">
          <DialogHeader>
            <DialogTitle>Invia relazione</DialogTitle>
            <DialogDescription>
              Ogni sezione visibile deve raggiungere il {COMPLETION_THRESHOLD}%. Dopo l&apos;invio la relazione diventa in sola lettura e lo schema verrà congelato in snapshot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[45vh] overflow-y-auto">
            {sections.map((s) => {
              const c = report.completion?.[s.key] || 0;
              const ok = c >= COMPLETION_THRESHOLD;
              const sezData = report.sections?.[s.key] || {};
              const missing = ok ? [] : computeMissingFields(s, sezData);
              return (
                <div key={s.key} className="text-sm px-2 py-1.5 rounded-lg bg-accent/20 space-y-1" data-testid={`submit-row-${s.key}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Circle className="h-3.5 w-3.5 text-red-500" />}
                      {s.nome}
                    </span>
                    <span className={`text-xs tabular-nums font-medium ${ok ? "text-emerald-500" : "text-red-500"}`}>{c}%</span>
                  </div>
                  {missing.length > 0 && (
                    <ul className="list-disc pl-6 text-[11px] text-red-600/90 dark:text-red-400/90 space-y-0.5" data-testid={`submit-missing-${s.key}`}>
                      {missing.map((f) => <li key={f.key}>{f.label}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
          {submitError && (
            <div className="rounded-lg bg-rose-50/60 dark:bg-rose-950/30 border border-rose-200/60 p-3 text-xs text-rose-800 dark:text-rose-200" data-testid="submit-error-detail">
              <div className="font-medium mb-1">Impossibile inviare: {submitError.detail || "sezioni sotto soglia"}</div>
              <ul className="list-disc pl-4 space-y-1">
                {(submitError.missing_sections || []).map((s) => {
                  // Retrocompat: se è stringa, la trattiamo come key (vecchio formato)
                  if (typeof s === "string") {
                    return <li key={s}>{sections.find((x) => x.key === s)?.nome || s} — {submitError.completion?.[s] ?? 0}%</li>;
                  }
                  return (
                    <li key={s.key}>
                      <span className="font-medium">{s.nome}</span> — {s.completion}%
                      {s.missing_fields?.length > 0 && (
                        <span className="text-rose-700/80 dark:text-rose-300/80"> ({s.missing_fields.map((f) => f.label).join(", ")})</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)}>Annulla</Button>
            <Button
              onClick={doSubmit}
              disabled={submitting || belowThreshold.length > 0}
              data-testid="submit-confirm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
